import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  animatedDigitStyle,
  animatedDigitWindowStyle,
  animatedRecordTimerStyle,
} from "./styles";

const SEC_ANIM_MS = 280;

export type AnimatedRecordTimerParts = {
  mins: string;
  secs: string;
  cents: string;
};

// Format elapsed ms as `0:00,00` → `0:01,00` (padded seconds + centiseconds).
export const formatAnimatedRecordTime = (totalMs: number): string => {
  const { mins, secs, cents } = splitRecordTime(totalMs);
  return `${mins}:${secs},${cents}`;
};

export const splitRecordTime = (totalMs: number): AnimatedRecordTimerParts => {
  const safe = Number.isFinite(totalMs) && totalMs > 0 ? totalMs : 0;
  const totalSeconds = Math.floor(safe / 1000);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  const hundredths = Math.floor((safe % 1000) / 10);
  return {
    mins: String(mins),
    secs: secs < 10 ? `0${secs}` : String(secs),
    cents: hundredths < 10 ? `0${hundredths}` : String(hundredths),
  };
};

export type AnimatedRecordTimerProps = {
  timeMs: number;
  showCentiseconds?: boolean;
  sx?: SxProps<Theme>;
  className?: string;
};

// One digit column — slides in from above when this digit changes.
const AnimatedDigit: React.FC<{ digit: string }> = ({ digit }) => {
  const [shown, setShown] = useState(digit);
  const [outgoing, setOutgoing] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const shownRef = useRef(digit);

  useEffect(() => {
    if (digit === shownRef.current) return;

    const previous = shownRef.current;
    shownRef.current = digit;
    setOutgoing(previous);
    setShown(digit);
    setIsAnimating(true);

    const timeoutId = setTimeout(() => {
      setOutgoing(null);
      setIsAnimating(false);
    }, SEC_ANIM_MS);

    return () => clearTimeout(timeoutId);
  }, [digit]);

  return (
    <Box component="span" sx={animatedDigitWindowStyle}>
      {outgoing != null && isAnimating ? (
        <Box component="span" sx={animatedDigitStyle("outgoing")}>
          {outgoing}
        </Box>
      ) : null}
      <Box
        component="span"
        key={shown}
        sx={animatedDigitStyle(isAnimating ? "incoming" : "static")}
      >
        {shown}
      </Box>
    </Box>
  );
};

export const AnimatedRecordTimer: React.FC<AnimatedRecordTimerProps> = ({
  timeMs,
  showCentiseconds = true,
  sx,
  className,
}) => {
  const { mins, secs, cents } = splitRecordTime(timeMs);
  const label = showCentiseconds
    ? `${mins}:${secs},${cents}`
    : `${mins}:${secs}`;

  return (
    <Typography
      component="span"
      className={className}
      sx={[animatedRecordTimerStyle, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      aria-label={label}
    >
      <Box component="span">{mins}:</Box>
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center" }}
      >
        {secs.split("").map((digit, index) => (
          <AnimatedDigit
            // Stable left-to-right keys: tens then ones (always 2 digits).
            key={`sec-digit-${index}`}
            digit={digit}
          />
        ))}
      </Box>
      {showCentiseconds ? <Box component="span">,{cents}</Box> : null}
    </Typography>
  );
};

